// ─── Top Risks (Overview redesign, replaces CrossRepoInsights) ─────────────
// CrossRepoInsights was written in Tailwind utility classes, but the dashboard
// does not load Tailwind, so it rendered as raw unstyled text with severity
// labels fused onto titles and patterns in backend order. This component uses
// the dashboard design tokens only (styles/overview.css) and sorts by severity
// so the most dangerous finding is always first.
import React from 'react';
import type { AggregateFindings } from '../api';

export type RiskPattern = AggregateFindings['patterns'][number];

export const TOP_RISKS_LIMIT = 5;

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  warning: 2,
  low: 3,
  info: 4,
};

export function severityRank(severity: string | null | undefined): number {
  return SEVERITY_RANK[(severity ?? '').toLowerCase()] ?? 5;
}

export type SeverityTone = 'danger' | 'warning' | 'info' | 'muted';

export function severityTone(severity: string | null | undefined): SeverityTone {
  const rank = severityRank(severity);
  if (rank <= 1) return 'danger';
  if (rank === 2) return 'warning';
  if (rank === 3) return 'info';
  return 'muted';
}

/** Severity first (critical → info), then the most widespread pattern first. */
export function sortRiskPatterns(patterns: RiskPattern[]): RiskPattern[] {
  return [...patterns].sort(
    (a, b) =>
      severityRank(a.severity) - severityRank(b.severity) ||
      (b.affectedProjects ?? 0) - (a.affectedProjects ?? 0),
  );
}

/** Display names of the repos that have this finding, in a stable order. */
export function repoNames(pattern: RiskPattern, projectNames: Record<string, string>): string[] {
  return (pattern.projectIds ?? [])
    .map((id: string) => projectNames[id])
    .filter((name: string | undefined): name is string => Boolean(name))
    .sort((a: string, b: string) => a.localeCompare(b));
}

/** "admin-portal", "admin-portal, twizzlelabs", "admin-portal +2 more", or the count fallback. */
export function repoLabel(pattern: RiskPattern, projectNames: Record<string, string>): string {
  const names = repoNames(pattern, projectNames);
  if (names.length === 0) return `${pattern.affectedProjects} of ${pattern.totalProjects} repos`;
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} +${names.length - 1} more`;
}

/** Chat prompt for one finding -- carries severity, title and the affected repos. */
export function buildFixPrompt(pattern: RiskPattern, projectNames: Record<string, string>): string {
  const names = repoNames(pattern, projectNames);
  const where = names.length > 0 ? ` in ${names.join(', ')}` : '';
  return `Fix this finding${where}:\n\n` +
    `**[${(pattern.severity || 'info').toUpperCase()}]** ${pattern.findingType}\n\n` +
    `Open a pull request with the fix and tell me what changed.`;
}

/** Chat prompt for every open finding, highest severity first, each with its repos. */
export function buildFixAllPrompt(patterns: RiskPattern[], projectNames: Record<string, string>): string {
  const sorted = sortRiskPatterns(patterns);
  const lines = sorted.map((p: RiskPattern, i: number) => {
    const names = repoNames(p, projectNames);
    const where = names.length > 0 ? ` — ${names.join(', ')}` : '';
    return `${i + 1}. **[${(p.severity || 'info').toUpperCase()}]** ${p.findingType}${where}`;
  });
  return `Fix all ${sorted.length} open findings across my repos, highest severity first:\n\n` +
    `${lines.join('\n')}\n\n` +
    `Open pull requests with the fixes and tell me what changed.`;
}

export function TopRisks({ aggregate, onFix, onFixAll, projectNames = {} }: {
  aggregate: AggregateFindings;
  onFix: (pattern: RiskPattern) => void;
  /** Fix every open finding in one chat request. */
  onFixAll?: () => void;
  /** project_id -> display name, used to name the affected repos. */
  projectNames?: Record<string, string>;
}) {
  const patterns = sortRiskPatterns(aggregate.patterns ?? []);
  const shown = patterns.slice(0, TOP_RISKS_LIMIT);
  const hidden = patterns.length - shown.length;
  const resolved = aggregate.resolutionRate?.last30Days?.resolved ?? 0;

  return (
    <section className="ov-card" aria-labelledby="ov-risks-h">
      <div className="ov-card-head">
        <div>
          <h3 id="ov-risks-h" className="ov-card-title">Top risks across your repos</h3>
          <div className="ov-card-sub">
            {aggregate.totalOpenFindings ?? 0} open · {resolved} resolved in the last 30 days
          </div>
        </div>
        {onFixAll && patterns.length > 1 && (
          <button type="button" className="btn btn-sm btn-primary" onClick={onFixAll}>
            Fix all {patterns.length}
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="ov-empty">No open findings</div>
      ) : (
        shown.map((p: RiskPattern, i: number) => (
          <div key={`${p.findingType}-${i}`} className="ov-risk-row">
            <span className={`ov-sev ov-sev--${severityTone(p.severity)}`}>
              {(p.severity || 'info').toUpperCase()}
            </span>
            <div className="ov-risk-title">{p.findingType}</div>
            <span className="ov-risk-scope">
              {p.affectedProjects} of {p.totalProjects} repos
            </span>
            <button
              type="button"
              className={`btn btn-sm${i === 0 && !(onFixAll && patterns.length > 1) ? ' btn-primary' : ''}`}
              onClick={() => onFix(p)}
            >
              Fix it
            </button>
          </div>
        ))
      )}

      {hidden > 0 && (
        <div className="ov-more">
          <a className="ov-link" href="#overview-projects">View all</a>
        </div>
      )}

      <div className="ov-card-foot">Fixes open as pull requests. Eligible fixes can merge automatically after required checks pass. You can review changes and follow-up fixes.</div>
    </section>
  );
}
